import React from "react";
import { Badge, Box, Typography, alpha, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getNavIcon } from "../navIcons";
import { getCatalogItemById } from "../../../constants/navCatalog";
import { dashIconWellSx, getDashAccent } from "../myDashboardStyles";
import DashIconDraw, { staggerFromId } from "../DashIconDraw";
import { usePendingTimeTrackingCount } from "../../../hooks/usePendingTimeTrackingCount";
import { useContestacionAvisos } from "../../Estimates/useContestacionAvisos";

export default function ShortcutWidget({ widget, editing }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const catalog = getCatalogItemById(widget.catalogId);
  const Icon = getNavIcon(catalog?.icon);
  const label = catalog ? t(catalog.labelKey) : widget.catalogId;
  const route = widget.route || catalog?.route;
  const accent = getDashAccent(
    catalog?.id || widget.catalogId,
    catalog?.section,
    theme.palette.mode,
  );
  const isControlHorario =
    catalog?.id === "control-horario" || widget.catalogId === "control-horario";
  const isEstimates =
    catalog?.id === "estimates" || widget.catalogId === "estimates";
  const { total: pendingHorario } = usePendingTimeTrackingCount();
  const { count: contestacionCount } = useContestacionAvisos(isEstimates);
  const showHorarioBadge = isControlHorario && pendingHorario > 0;
  const showEstimatesBadge = isEstimates && contestacionCount > 0;
  const badgeCount = showEstimatesBadge ? contestacionCount : pendingHorario;
  const showBadge = showEstimatesBadge || showHorarioBadge;
  const drawDelay = staggerFromId(widget.i || widget.catalogId);

  const handleClick = () => {
    if (editing || !route) return;
    navigate(route);
  };

  return (
    <Box
      onClick={handleClick}
      role={editing ? undefined : "button"}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 1.25,
        p: 1.5,
        cursor: editing ? "default" : "pointer",
        borderRadius: 2,
        "&:hover .shortcut-icon": editing
          ? undefined
          : {
              bgcolor: alpha(
                accent,
                theme.palette.mode === "dark" ? 0.22 : 0.18,
              ),
              boxShadow: `0 0 0 1px ${alpha(accent, 0.4)}, 0 10px 28px ${alpha(accent, 0.28)}`,
            },
        "&:hover .shortcut-label": editing
          ? undefined
          : { color: "text.primary" },
      }}
    >
      <DashIconDraw
        className="shortcut-icon"
        delay={drawDelay}
        sx={[
          (th) => ({
            ...dashIconWellSx(th, accent),
            width: { xs: 40, sm: 44 },
            height: { xs: 40, sm: 44 },
            borderRadius: 1.75,
          }),
          {
            transition: "background-color 0.18s ease",
          },
        ]}
      >
        <Badge
          badgeContent={badgeCount}
          invisible={!showBadge}
          max={99}
          overlap="circular"
          sx={{
            "& .MuiBadge-badge": {
              backgroundColor: "#e53935",
              color: "#fff",
              fontWeight: 800,
              fontSize: "0.68rem",
              minWidth: 18,
              height: 18,
              borderRadius: "50%",
              border: `2px solid ${theme.palette.background.paper}`,
            },
          }}
        >
          <Icon sx={{ fontSize: { xs: 20, sm: 22 } }} />
        </Badge>
      </DashIconDraw>
      <Typography
        className="shortcut-label"
        variant="body2"
        fontWeight={700}
        textAlign="center"
        sx={{
          lineHeight: 1.25,
          letterSpacing: 0.1,
          px: 0.5,
          transition: "color 0.18s ease",
          fontSize: { xs: "0.72rem", sm: "0.8rem" },
          wordBreak: "break-word",
          hyphens: "auto",
          maxWidth: "100%",
          color: "text.secondary",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}
